import graphql from "babel-plugin-relay/macro";
import classNames from "classnames";
import { AddressFields } from "components/address/AddressFields";
import styles from "components/address/AddressForm.module.scss";
import { AddressSearch, AddressSelection } from "components/address/AddressSearch";
import { AddressStateWarning } from "components/address/AddressStateWarning";
import { AddressSummary } from "components/address/AddressSummary";
import { JurisdictionModal } from "components/jurisdiction/JurisdictionModal";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { FinishedLoadingNotification } from "components/shared/FinishedLoadingNotification";
import { SpinnerButton } from "components/shared/SpinnerButton";
import { accountMessages } from "domain/account/accountMessages";
import { MutatableSetAddressesMutationVariables, setAddresses } from "domain/address/setAddresses";
import { toastActions } from "domain/toast/toastActions";
import { AddressFormJurisdictionQuery, AddressFormJurisdictionQueryVariables } from "generatedQueries/AddressFormJurisdictionQuery.graphql";
import { AddressFormQuery, AddressFormQueryResponse } from "generatedQueries/AddressFormQuery.graphql";
import { LicensingAccountAddressInput, setAddressesMutationResponse } from "generatedQueries/setAddressesMutation.graphql";
import { Mutable } from "infrastructure/Mutable";
import { forEach, omit, trim } from "lodash";
import { FC, useState } from "react";
import { DefaultValues, FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { MessageDescriptor, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useLazyLoadQuery, useRelayEnvironment } from "react-relay";
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { fetchQuery, PayloadError } from "relay-runtime";
import { Message } from "shared/components/Message";
import { WarningMessage } from "shared/components/WarningMessage";
import { Watch } from "shared/components/Watch";
import { mapMessages } from "shared/mapMessages";
import { sharedMessages } from "shared/sharedMessages";

const messages = mapMessages("components.address.AddressForm", {
    errorRetrievingJurisdictions: "There was an error retrieving the jurisdictions for your address, please try again later.",
    mailingAddressTypeLabel: "Mailing address type",
});

export enum AddressType {
    Physical,
    Mailing,
}

type AddressFormValues = MutatableSetAddressesMutationVariables & {
    postalAddressType?: "postalIsSameAsPhysical" | "streetAddress" | "poBox" | null;
};

interface IProps {
    nextButtonMessage?: MessageDescriptor;
    saveButtonMessage?: MessageDescriptor;
    onBack: () => void;
    onNext: (skip?: number) => void;
    isNewAccount: boolean;
    pageContentName: MessageDescriptor | string;
}

export const AddressForm: FC<IProps> = ({
    nextButtonMessage,
    saveButtonMessage,
    onBack,
    onNext,
    isNewAccount,
    pageContentName,
}) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const environment = useRelayEnvironment();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasAddressStateError, setHasAddressStateError] = useState(false);
    const [isJurisdictionSelectionModalDisplayed, setIsJurisdictionSelectionModalDisplayed] = useState(false);
    const [jurisdictions, setJurisdictions] = useState<{ jurisdictionId: number, name: string }[]>();
    const [hasErrorRetrievingJurisdictions, setHasErrorRetrievingJurisdictions] = useState(false);
    const [validationErrorMessage, setValidationErrorMessage] = useState("");

    const { account, configuration } = useLazyLoadQuery<AddressFormQuery>(formQuery, {});

    const useFormReturn = useForm<AddressFormValues>({
        defaultValues: account ? getInitialValues(isNewAccount, account) : undefined,
        mode: "onBlur",
    });
    const {
        handleSubmit,
        register,
        setValue,
        watch,
        formState: { isDirty },
    } = useFormReturn;

    const postalIsSameAsPhysicalAddress = watch("postalAddressType");

    if (!account) {
        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
    }

    return <div aria-busy="false">
        <FinishedLoadingNotification contentName={pageContentName} />
        <FormProvider {...useFormReturn}>
            <Form onSubmit={handleSubmit(onFormSubmitted)} noValidate={true} role="form">
                <>
                    <Row>
                        <Col
                            lg={6}
                            role="region"
                            aria-labelledby="physicalAddressHeading"
                        >
                            <Card className={classNames("mt-3", styles.addressCard)}>
                                <CardHeader id="physicalAddressHeading">
                                    <Message message={accountMessages.physicalAddressLabel} />
                                </CardHeader>
                                <CardBody>
                                    {
                                        configuration?.isAddressSearchEnabled &&
                                        <>
                                            <AddressSearch
                                                id="physicalAddressSearch"
                                                onAddressSelected={onPhysicalAddressSelected}
                                            />
                                            <hr />
                                        </>
                                    }
                                    <AddressFields
                                        namePrefix="physicalAddress"
                                        onPostcodeChange={clearJurisdiction}
                                        onStateChange={clearJurisdiction}
                                        onSuburbChange={clearJurisdiction}
                                    />
                                    <input type="hidden" {...register("physicalAddress.jurisdictionId")} />
                                </CardBody>
                            </Card>
                        </Col>
                        <Col
                            lg={6}
                            role="region"
                            aria-labelledby="mailingAddressHeading"
                        >
                            <Card className={classNames("mt-3", styles.addressCard)}>
                                <CardHeader id="mailingAddressHeading">
                                    <Message message={accountMessages.postalAddressLabel} />
                                </CardHeader>
                                <CardBody>
                                    <div
                                        className="mb-3"
                                        role="radiogroup"
                                        aria-label={formatMessage(messages.mailingAddressTypeLabel)}
                                    >
                                        <FormGroup check={true} inline={true}>
                                            <Label check={true}>
                                                <Input type="radio" value="postalIsSameAsPhysical" {...registerWithInnerRef(register, "postalAddressType")}/>
                                                <Message message={accountMessages.postalIsSameAsPhysicalAddressLabel} />
                                            </Label>
                                        </FormGroup>
                                        <FormGroup check={true} inline={true}>
                                            <Label check={true}>
                                                <Input type="radio" value="streetAddress" {...registerWithInnerRef(register, "postalAddressType")} />
                                                <Message message={accountMessages.streetAddressLabel} />
                                            </Label>
                                        </FormGroup>
                                        <FormGroup check={true} inline={true}>
                                            <Label check={true}>
                                                <Input type="radio" value="poBox"  {...registerWithInnerRef(register, "postalAddressType")} />
                                                <Message message={accountMessages.poBoxLabel} />
                                            </Label>
                                        </FormGroup>
                                    </div>
                                    {
                                        postalIsSameAsPhysicalAddress !== "postalIsSameAsPhysical" &&
                                        <>
                                            {
                                                postalIsSameAsPhysicalAddress === "streetAddress" && configuration?.isAddressSearchEnabled &&
                                                <>
                                                    <AddressSearch
                                                        id="mailingAddressSearch"
                                                        onAddressSelected={onMailingAddressSelected}
                                                    />
                                                    <hr />
                                                </>
                                            }
                                            <AddressFields namePrefix="mailingAddress" displayPoBoxFields={postalIsSameAsPhysicalAddress === "poBox"} />
                                        </>
                                    }
                                    {
                                        postalIsSameAsPhysicalAddress === "postalIsSameAsPhysical" &&
                                        <Watch
                                            name="physicalAddress"
                                            render={(physicalAddressValue) => <AddressSummary {...physicalAddressValue} className="mt-3" />}
                                        />
                                    }
                                </CardBody>
                            </Card>
                        </Col>
                        <Col sm={12}>
                            {
                                hasAddressStateError && <AddressStateWarning />
                            }
                            {
                                validationErrorMessage && <WarningMessage message={validationErrorMessage} />
                            }
                            <Button
                                color="secondary"
                                type="button"
                                className="float-left"
                                onClick={onBack}>
                                <Message message={sharedMessages.backButtonLabel} />
                            </Button>
                            {
                                isNewAccount || isDirty
                                    ? <SpinnerButton
                                        className="float-right"
                                        color="primary"
                                        isLoading={isSubmitting}
                                        message={saveButtonMessage || sharedMessages.saveLabel} />
                                    : <Button
                                        className="float-right"
                                        color="primary"
                                        type="button"
                                        onClick={onNextButtonClicked}>
                                        <Message message={nextButtonMessage || sharedMessages.nextButtonLabel} />
                                    </Button>
                            }
                        </Col>
                    </Row>
                    {
                        hasErrorRetrievingJurisdictions &&
                        <Row>
                            <Col>
                                <ErrorMessage
                                    message={messages.errorRetrievingJurisdictions}
                                />
                            </Col>
                        </Row>
                    }
                    <JurisdictionModal
                        isSaving={isSubmitting}
                        show={isJurisdictionSelectionModalDisplayed}
                        jurisdictions={jurisdictions ?? []}
                        onCancelled={onJurisdictionModalCancelled}
                        onConfirmed={onJurisdictionModalConfirmed}
                        configuration={configuration}
                    />
                </>
            </Form>
        </FormProvider>
    </div>;

    function onNextButtonClicked() {
        onNext();
    }

    function onPhysicalAddressSelected(selection: AddressSelection) {
        setValue("physicalAddress.addressLine", selection?.addressLine ?? "");
        setValue("physicalAddress.country", selection?.country ?? "");
        setValue("physicalAddress.postcode", selection?.postcode ?? "");
        setValue("physicalAddress.state", selection?.state ?? "");
        setValue("physicalAddress.suburb", selection?.suburb ?? "");
    }

    function onMailingAddressSelected(selection: AddressSelection) {
        setValue("mailingAddress.addressLine", selection?.addressLine ?? "");
        setValue("mailingAddress.country", selection?.country ?? "");
        setValue("mailingAddress.postcode", selection?.postcode ?? "");
        setValue("mailingAddress.state", selection?.state ?? "");
        setValue("mailingAddress.suburb", selection?.suburb ?? "");
    }

    function clearJurisdiction() {
        setValue("physicalAddress.jurisdictionId", null);
    }

    function onJurisdictionModalCancelled() {
        setIsJurisdictionSelectionModalDisplayed(false);
        setIsSubmitting(false);
    }

    function onJurisdictionModalConfirmed(jurisdictionId: number) {
        setValue("physicalAddress.jurisdictionId", jurisdictionId);
        handleSubmit(onFormSubmitted)();
    }

    function onFormSubmitted(values: AddressFormValues) {
        setIsSubmitting(true);
        setHasAddressStateError(false);
        setHasErrorRetrievingJurisdictions(false);
        setValidationErrorMessage("");

        const variables: AddressFormJurisdictionQueryVariables = {
            postcode: values.physicalAddress.postcode,
            state: values.physicalAddress.state,
            suburb: values.physicalAddress.suburb,
        };

        if (values.physicalAddress.jurisdictionId) {
            saveAddress(values.physicalAddress.jurisdictionId);
            return;
        }

        fetchQuery<AddressFormJurisdictionQuery>(environment, jurisdictionQuery, variables)
            .subscribe({
                error() {
                    setHasErrorRetrievingJurisdictions(true);
                    setIsSubmitting(false);
                },
                next(response) {
                    if (response.licensingJurisdictionSearch?.length === 1) {
                        saveAddress(response.licensingJurisdictionSearch[0].jurisdictionId);
                    } else {
                        setJurisdictions(response.licensingJurisdictionSearch?.map(j => ({ ...j })) ?? []);
                        setIsJurisdictionSelectionModalDisplayed(true);
                        setIsSubmitting(false);
                    }
                },
            });

        function saveAddress(jurisdictionId: number) {
            let mailingAddress: MutatableSetAddressesMutationVariables["mailingAddress"] = {};
            switch(values.postalAddressType){
                case "postalIsSameAsPhysical":
                    mailingAddress = { ...values.physicalAddress, jurisdictionId: null };
                    break;
                case "poBox":
                    mailingAddress = { ...omit(values.mailingAddress, "addressLine") };
                    break;
                case "streetAddress":
                    mailingAddress = { ...omit(values.mailingAddress, "deliveryTypeId", "deliveryNumber" ) };
                    break;
            }

            const setAddressVariables: MutatableSetAddressesMutationVariables = {
                mailingAddress: trimValues(mailingAddress),
                physicalAddress: trimValues({ ...values.physicalAddress, jurisdictionId }),
            };

            setAddresses(environment, setAddressVariables, onSaveCompleted, onError);
        }
    }

    function onSaveCompleted(response: setAddressesMutationResponse, errors?: PayloadError[] | null) {
        if ((errors && errors.length) || !response.updatePhysicalAddress?.result || !response.updateMailingAddress?.result) {
            if (response.updatePhysicalAddress?.errors?.length) {
                checkErrorCode(response.updatePhysicalAddress.errors[0].errorCode, response.updatePhysicalAddress.errors[0].message);
            }
            else if (response.updateMailingAddress?.errors?.length) {
                checkErrorCode(response.updateMailingAddress.errors[0].errorCode, response.updateMailingAddress.errors[0].message);
            }
            onError();
            return;
        }

        dispatch(toastActions.toastSuccess());
        onNext();
    }

    function checkErrorCode(code: string, message: string) {
        if (code === "STATENOTFOUND") {
            setHasAddressStateError(true);
        }
        else if (code === "VALIDATIONERROR") {
            setValidationErrorMessage(message);
        }
    }

    function onError() {
        dispatch(toastActions.toastFailure());
        setIsSubmitting(false);
    }
};

function getInitialValues(isNewAccount: boolean, account: Exclude<AddressFormQueryResponse["account"], null>): DefaultValues<AddressFormValues> {
    let postalAddressType: AddressFormValues["postalAddressType"];
    if (isNewAccount) {
        postalAddressType = "postalIsSameAsPhysical";
    } else if (account.physicalAddress && account.mailingAddress) {
        if (account.physicalAddress.id === account.mailingAddress.id) {
            postalAddressType = "postalIsSameAsPhysical";
        } else {
            postalAddressType = account.mailingAddress.__typename === "LicensingDeliveryAddress"
                ? "poBox"
                : "streetAddress";
        }
    } else {
        postalAddressType = "postalIsSameAsPhysical";
    }
    return {
        mailingAddress: { ...omit(account.mailingAddress, "__typename", "deliveryType"), deliveryTypeId: account.mailingAddress?.deliveryTypeId ?? null },
        physicalAddress: { ...account.physicalAddress },
        postalAddressType,
    };
}

function trimValues(address: Mutable<LicensingAccountAddressInput>): Mutable<LicensingAccountAddressInput> {
    forEach(address, (value, key) => {
        if (typeof value === "string") {
            address[key] = trim(value);
        }
    });

    return address;
}

function registerWithInnerRef(register: UseFormReturn<AddressFormValues>["register"], ...params: Parameters<UseFormReturn<AddressFormValues>["register"]>) {
    const { ref: innerRef, ...rest } = register(...params);
    return { innerRef, ...rest };
}

const formQuery = graphql`
query AddressFormQuery {
    account {
        physicalAddress {
            id
            addressLine
            suburb
            state
            postcode
            jurisdictionId
        }
        mailingAddress {
            __typename
            ... on LicensingAddress {
                id
                postcode
                state
                suburb
            }
            ... on LicensingStreetAddress {
                jurisdictionId
                addressLine
            }
            ... on LicensingDeliveryAddress {
                deliveryTypeId
                deliveryNumber
            }
        }
    }
    configuration {
        isAddressSearchEnabled
        ...JurisdictionModal_configuration
    }
}`;

const jurisdictionQuery = graphql`
    query AddressFormJurisdictionQuery($state: String, $suburb: String, $postcode: String) {
        licensingJurisdictionSearch(state: $state, suburb: $suburb, postcode: $postcode) {
            jurisdictionId
            name
        }
    }
`;
